/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useEffect } from 'react'
import { intCol, numCol } from '@integec/grid-tools/lib/cols'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import {
  modifierCol,
  modifiedDateCol,
  utilityHeader,
  unitHeader,
} from 'core/HeaderColTypeProps'
import { getApiTargetInfo, UtilityUnitSelection } from 'core'
import { connect } from 'react-redux'
import R from 'ramda'

import {
  postNERCCapacityInfo,
  deleteNERCCapacityInfo,
  getNERCCapacityInfoOnUnit,
} from 'api/pgads.js'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getUtilityUnitsMap,
  getPermitActions,
  getEnvConfig,
} from 'core/selectors'
import CommonScreen from './CommonScreen'
import { Select, Button, InputNumber } from 'antd'

import { hasAccesPermits } from 'core/utils'
import * as ScreenPermissions from 'core/EventActions'

const { Option } = Select

// const retrieveTask = apiTargetInfo => () => getNERCCapacityInfo(apiTargetInfo)

const retrieveTask = apiTargetInfo => units =>
  getNERCCapacityInfoOnUnit(apiTargetInfo)(units)

const modifyTask = apiTargetInfo => data =>
  postNERCCapacityInfo(apiTargetInfo)(data)

const deleteTask = apiTargetInfo => data =>
  deleteNERCCapacityInfo(apiTargetInfo)(data)

const monthOption = R.range(1, 13).map(i => ({
  key: `${i}`,
  value: `${i}`,
  text: `${i}`,
}))

const headers = [
  utilityHeader,
  unitHeader,
  intCol({
    ident: 'month',
    display: 'Month',
    isKey: true,
  }),
  numCol({
    ident: 'netDepCap',
    display: 'Net Dependable Capacity',
    isRequired: true,
  }),
  numCol({
    ident: 'grossDepCap',
    display: 'Gross Dependable Capacity',
  }),
  modifierCol,
  modifiedDateCol,
]

const title = 'NERC Capacity'

const NERCCapacity = props => {
  const [insertEvent, setInsertEvent] = useState(false)
  // const [] = useState({ netDepCap: 0, grossDepCap: 0 })
  const [withoutModify, setWithoutModify] = useState(true)

  const {
    utilityNameMap,
    unitNameMap,
    utilityUnitMap,
    envConfig,
    actionPermits,
  } = props

  const generateDataGetter = ({ rowData, header }) => {
    if (header.ident === 'unit') header.choices = []
    if (rowData.utility !== '' && rowData.utility !== undefined) {
      if (header.ident === 'unit') {
        const unitList = utilityUnitMap && utilityUnitMap[rowData.utility]
        header.choices = unitList
          ? unitList.map(value => ({
              text: unitNameMap[value] || value,
              value,
            }))
          : []

        return rowData.unit
      }
    }
  }

  useEffect(() => {
    const isAdmin = hasAccesPermits({
      authGodMode: envConfig.authGodMode,
      actionPermits,
      eventActions: [ScreenPermissions.NERC_CAPACITY_USER_ADMIN],
      matchAny: true,
    })
    setWithoutModify(!isAdmin)
  }, [actionPermits, envConfig.authGodMode])

  const withImport = hasAccesPermits({
    authGodMode: envConfig.authGodMode,
    actionPermits,
    eventActions: [ScreenPermissions.NERCCAP_IMPORT],
    matchAny: true,
  })

  const headerWithFormatter = headers.map(header =>
    header.ident === 'utility'
      ? {
          ...header,
          ...toSelectionColProps(utilityNameMap),
          dataFormatter: ({ value }) => utilityNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            utilityNameMap[rowData.utility] || rowData.utility,
        }
      : header.ident === 'unit'
      ? {
          ...header,
          choices: [],
          dataGetter: generateDataGetter,
          dataFormatter: ({ value }) => unitNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            unitNameMap[rowData.unit] || rowData.unit,
        }
      : header.ident === 'month'
      ? {
          ...header,
          choices: monthOption,
        }
      : header
  )

  const exportHeader = headers.map(header =>
    header.ident === 'unit'
      ? {
          ...header,
          dataGetter: ({ rowData }) => rowData?.unit?.substr(3),
        }
      : header
  )

  const insertComp = ({ insertData, setInsertData, onAdd, onCancel }) => (
    <table
      style={{
        width: '100%',
        borderSpacing: '10px',
        borderCollapse: 'inherit',
      }}
    >
      <tbody>
        <tr>
          <td style={{ minWidth: '100px' }}>Utility / Unit:</td>
          <td style={{ minWidth: '450px' }}>
            <UtilityUnitSelection
              className="utilityUnit"
              utility={insertData.utility}
              unit={insertData.unit}
              onUnitChange={value => {
                // setUtilityUnit(value)
                setInsertData({
                  ...insertData,
                  utility: value[0],
                  unit: value[1],
                })
              }}
            />
          </td>
          <td style={{ width: '200px' }}>Net Dependable Capacity:</td>
          <td>
            <InputNumber
              className="netDependableCapacity"
              defaultValue={0}
              onChange={value =>
                setInsertData({ ...insertData, netDepCap: value })
              }
            />
          </td>
          <td />
        </tr>
        <tr>
          <td>Month :</td>
          <td style={{ minWidth: '150px' }}>
            <Select
              className="month"
              value={insertData.month}
              style={{ minWidth: '150px' }}
              onChange={value => setInsertData({ ...insertData, month: value })}
            >
              {monthOption.map(m => (
                <Option key={m} value={m.key}>
                  {m.value}
                </Option>
              ))}
            </Select>
          </td>

          <td>Gross Dependable Capacity: </td>
          <td>
            <InputNumber
              className="grossDependableCapacity"
              defaultValue={0}
              onChange={value =>
                setInsertData({ ...insertData, grossDepCap: value })
              }
            />
          </td>
          <td>
            <Button type="primary" onClick={() => onAdd(true)}>
              Add
            </Button>
          </td>
          <td>
            <Button type="primary" onClick={() => onAdd(false)}>
              Add & Hide
            </Button>
          </td>
          <td>
            <Button type="danger" onClick={onCancel}>
              Cancel
            </Button>
          </td>
        </tr>
      </tbody>
    </table>
  )

  return (
    <CommonScreen
      key="nerccap"
      title={title}
      isAdminScreen={false}
      header={headerWithFormatter}
      exportHeader={exportHeader}
      saveTask={modifyTask}
      retrieveTask={retrieveTask}
      withCriteria={(state, props, selectedUnits) => selectedUnits}
      withUnitSelection
      deleteTask={deleteTask}
      autoRetrieve={false}
      altBy={d => d.unit}
      sortOptions={[utilityHeader, unitHeader]}
      allowInsert={!withoutModify}
      insertComp={insertEvent && insertComp}
      insertCompHeight={150}
      onInsertEvent={b => setInsertEvent(b)}
      headerRowHeight={60}
      defaultInsertData={{ netDepCap: 0, grossDepCap: 0, month: 1 + '' }}
      withoutModify={withoutModify}
      withImport={withImport}
      importUrl="screen-csv-import-file"
      importData={{ importType: 'NERC_CAPACITY' }}
    />
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  utilityUnitMap: getUtilityUnitsMap(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
})

export default connect(mapStateToProps)(NERCCapacity)
